import React, { useMemo } from 'react'
import { FaMapMarkerAlt, FaPlus } from 'react-icons/fa'
import { FoBtn, Pill } from './fieldUi'
import { useField } from './fieldContext'
import { nearbyCandidates, unroutedStores } from '../store'
import { fmtMiles, rankNearby } from '../geo'

/**
 * Stores close to a point that are NOT in today's plan, ranked by why they are worth a stop (missed visit, behind goal,
 * long time without a visit) and by distance. Salesperson only. Used in the route screen, the visit screen and the
 * "visit saved" sheet. Stores that have no route yet can't be added: the salesperson asks the office from here.
 */
export default function NearbyPanel({ anchor, limit, selectedId, onSelect, compact, onAdded }) {
    const f = useField()
    const excluded = f.stops.filter((s) => s.kind === 'visit').map((s) => s.store.id)
    const key = excluded.join(',')
    const cands = useMemo(
        () => nearbyCandidates(f.state.sales, f.actor.rep, excluded, f.vIdx, f.oIdx, unroutedStores()),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [f.state.sales, f.actor.rep, key]
    )
    const ranked = useMemo(() => (anchor ? rankNearby(anchor, cands, 15).slice(0, limit || 8) : []), [anchor, cands, limit])

    const add = (store, where) => {
        f.dispatch({ type: 'VISIT_EXTRA', storeId: store.id, rep: f.actor.rep, date: f.date, source: 'nearby', nextUp: where === 'next' })
        f.toast(store.name + ' added to ' + (where === 'next' ? 'your next stop' : 'the end of your route') + '.')
        if (onAdded) onAdded(store)
    }
    const askOffice = (store) => {
        f.send({ type: 'CHAT_SEND', actorKey: f.actor.key, from: 'worker', by: f.actor.name, text: 'Can you add “' + store.name + '” (' + store.city + ') to my route? It is close to where I am today.', topic: 'schedule', ref: { type: 'store', id: store.id, label: store.name } })
        f.toast('Message sent to the office.')
    }

    if (!anchor) return <div className="fo-muted">Turn on location to see stores near you.</div>
    if (ranked.length === 0) return <div className="fo-muted">No other stores within 15 miles of here.</div>
    return (
        <div className={'fo-nearby' + (compact ? ' fo-nearby--compact' : '')}>
            {ranked.map((r) => (
                <div key={r.store.id} className={'fo-nearby__row' + (selectedId === r.store.id ? ' is-on' : '')} onClick={onSelect ? () => onSelect(r.store.id) : undefined}>
                    <div className="fo-nearby__main">
                        <strong>{r.store.name}</strong>
                        <span><FaMapMarkerAlt /> {fmtMiles(r.dist)} away · {r.store.city}</span>
                        <div className="fo-nearby__why">{r.reasons.slice(0, compact ? 2 : 3).map((x, i) => <Pill key={i} tone={x.tone}>{x.text}</Pill>)}</div>
                    </div>
                    <div className="fo-nearby__act">
                        {r.unrouted
                            ? <FoBtn kind="soft" small onClick={() => askOffice(r.store)}>Ask the office</FoBtn>
                            : <React.Fragment>
                                <FoBtn kind="primary" small icon={<FaPlus />} onClick={() => add(r.store, 'next')}>Next</FoBtn>
                                {compact ? null : <FoBtn kind="soft" small onClick={() => add(r.store, 'end')}>At the end</FoBtn>}
                            </React.Fragment>}
                    </div>
                </div>
            ))}
        </div>
    )
}
